import { Fragment } from "react"
import { Box, Divider, SxProps, Typography } from "@mui/material"
import _ from "lodash"
import { ImdfFeature } from "providers/venue/types"
import { useTranslation } from "providers/i18n"
import { VerticalDirectoryOccupantCard } from "components/domains/directory/VerticalDirectoryOccupantCard"
import { formatLevelName } from "utils/feature"

interface IDirectoryGroupLayoutProps {
  pages: Array<{ data: ImdfFeature[] }>
  gridItemSx?: SxProps
  levelHeaderSx?: SxProps
  dividerSx?: SxProps
}

export const DirectoryGroupLayout = ({
  pages,
  gridItemSx,
  levelHeaderSx,
  dividerSx,
}: IDirectoryGroupLayoutProps) => {
  const { t } = useTranslation()
  const renderedLevels = new Set<string>()

  return (
    <>
      {pages.map((page, pageNum) => {
        // Group occupants by level_name
        const groupedByLevel = _.groupBy(page.data, "properties.level_name")
        // Sort groups by ordinal (descending)
        const sortedGroups = _.orderBy(
          Object.entries(groupedByLevel),
          ([, occupants]) => _.get(occupants, "[0].properties.ordinal", 0),
          ["desc"]
        )

        return (
          <Fragment key={pageNum}>
            {sortedGroups.map(([level, occupants]) => {
              const shouldShowHeader = !renderedLevels.has(level)
              if (shouldShowHeader) {
                renderedLevels.add(level)
              }

              const levelKey = formatLevelName(level)

              return (
                <Fragment key={`${pageNum}-${level}`}>
                  {shouldShowHeader && (
                    <Box sx={levelHeaderSx}>
                      <Typography>{t(`Level Name.${levelKey}`)}</Typography>
                      <Divider sx={dividerSx} />
                    </Box>
                  )}
                  {occupants.map((occupant) => {
                    const isAd = occupant?.properties?.isAd
                    const occupantId = String(occupant?.id)
                    const isNewShop = occupant?.properties?.isNewShop
                    const isComingSoon = occupant?.properties?.isComingSoon

                    return (
                      <Box
                        id={occupantId}
                        data-testid={occupantId}
                        sx={gridItemSx}
                        key={`${isAd ? "ad-" : "directory-item-"}${occupantId}`}
                      >
                        <VerticalDirectoryOccupantCard
                          data={occupant}
                          isNewShop={isNewShop}
                          isComingSoon={isComingSoon}
                        />
                      </Box>
                    )
                  })}
                </Fragment>
              )
            })}
          </Fragment>
        )
      })}
    </>
  )
}
